/**
 * Thumbnail rendering cache.
 *
 * Caches two things:
 * 1. Downscaled Image objects from base64 ref_photo_data (shared across all thumbnails)
 * 2. Rendered thumbnail data URLs keyed by look ID + dimensions (avoids re-drawing canvases)
 */

const MAX_REF_SIZE = 640; // max dimension for cached reference images
const refImageCache = new Map(); // base64 hash → downscaled Image
const thumbnailDataUrlCache = new Map(); // cacheKey → dataURL string

// Simple hash of first/last chars + length (fast, good enough for dedup)
const hashBase64 = (data) => {
  if (!data) return null;
  return `${data.length}_${data.charCodeAt(30) || 0}_${data.charCodeAt(data.length - 10) || 0}`;
};

/**
 * Get or create a downscaled Image from base64 data.
 * Returns a Promise<Image> that resolves with an image max MAX_REF_SIZE in either dimension.
 */
export const getRefImage = (base64Data) => {
  if (!base64Data) return Promise.resolve(null);

  const hash = hashBase64(base64Data);
  if (refImageCache.has(hash)) return refImageCache.get(hash);

  const promise = new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // Downscale if needed
      if (img.width <= MAX_REF_SIZE && img.height <= MAX_REF_SIZE) {
        resolve(img);
        return;
      }

      const canvas = document.createElement('canvas');
      const scale = Math.min(MAX_REF_SIZE / img.width, MAX_REF_SIZE / img.height);
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const scaled = new Image();
      scaled.onload = () => resolve(scaled);
      scaled.src = canvas.toDataURL('image/png');
    };
    img.onerror = () => resolve(null);
    img.src = base64Data;
  });

  refImageCache.set(hash, promise);
  return promise;
};

/**
 * Get a cached thumbnail data URL if available.
 */
export const getCachedThumbnail = (lookId, width, height) => {
  return thumbnailDataUrlCache.get(`${lookId}_${width}_${height}`) || null;
};

/**
 * Store a rendered thumbnail data URL.
 */
export const setCachedThumbnail = (lookId, width, height, dataUrl) => {
  thumbnailDataUrlCache.set(`${lookId}_${width}_${height}`, dataUrl);
};

/**
 * Invalidate thumbnails for a specific look (e.g., after editing).
 */
export const invalidateThumbnails = (lookId) => {
  for (const key of thumbnailDataUrlCache.keys()) {
    if (key.startsWith(`${lookId}_`)) {
      thumbnailDataUrlCache.delete(key);
    }
  }
};

/**
 * Clear all caches.
 */
export const clearThumbnailCache = () => {
  refImageCache.clear();
  thumbnailDataUrlCache.clear();
};
